import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Code,
  Image,
  FileText,
  Terminal,
  Globe,
  Search,
  Edit,
  Folder,
  Database,
  Zap,
  Activity,
  Cpu,
  Binary,
  CircuitBoard,
  Sparkles,
  Bot,
  Command,
  Layers,
  Network,
  Signal,
  Shield,
  Braces,
} from "lucide-react";
import {
  firstImageUrl,
  isBrowserScreenshotTool,
} from "../../utils/toolImages";

const Monitor = ({ messages = [], agentStatus = "stopped" }) => {
  const [currentToolCall, setCurrentToolCall] = useState(null);
  const [displayedContent, setDisplayedContent] = useState("");
  const [animationKey, setAnimationKey] = useState(0);
  const [agentStartMessageCount, setAgentStartMessageCount] = useState(null);

  const typeTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (agentStatus === "generating" && agentStartMessageCount === null) {
      setAgentStartMessageCount(messages?.length || 0);
    } else if (agentStatus === "stopped") {
      setAgentStartMessageCount(null);
    }
  }, [agentStatus, agentStartMessageCount, messages]);

  // Auto-scroll effect - always keep at bottom during streaming
  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      // Always scroll to bottom when content is being displayed (streaming)
      if (displayedContent && agentStatus === "generating") {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [displayedContent, agentStatus]);

  // Additional scroll effect for when tool call changes
  useEffect(() => {
    if (scrollContainerRef.current && currentToolCall) {
      const scrollContainer = scrollContainerRef.current;
      setTimeout(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }, 100);
    }
  }, [currentToolCall]);

  // Filter for only the most entertaining tool calls
  const interestingToolCalls = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    if (agentStatus === "generating" && agentStartMessageCount !== null) {
      // Only show messages that came after the agent started generating
      const newMessages = messages.slice(agentStartMessageCount);
      // Then filter for tool calls
      return newMessages.filter((msg) => msg.message_type === "tool_result");
    }

    // If not generating, show all tool calls
    return messages.filter((msg) => msg.message_type === "tool_result");
  }, [messages, agentStatus, agentStartMessageCount]);

  // Get the latest streaming tool call OR most recent completed tool call
  const latestToolCall = useMemo(() => {
    const streamingTool = interestingToolCalls.find((msg) => {
      const isStreaming =
        msg.isStreaming === true ||
        msg.is_streaming === true ||
        (typeof msg.isStreaming === "undefined" &&
          typeof msg.is_streaming !== "undefined" &&
          msg.is_streaming === true);
      return isStreaming;
    });

    if (streamingTool) return streamingTool;

    if (agentStatus === "generating" && interestingToolCalls.length > 0) {
      return interestingToolCalls[interestingToolCalls.length - 1];
    }

    return null;
  }, [interestingToolCalls, agentStatus]);

  // Get tool icon based on tool name/description
  const getToolIcon = (toolName) => {
    const name = (toolName || "").toLowerCase();

    if (name.includes("screenshot") || name.includes("image")) return Image;
    if (
      name.includes("file") ||
      name.includes("write") ||
      name.includes("edit")
    )
      return FileText;
    if (
      name.includes("terminal") ||
      name.includes("command") ||
      name.includes("run")
    )
      return Terminal;
    if (
      name.includes("browser") ||
      name.includes("url") ||
      name.includes("web")
    )
      return Globe;
    if (
      name.includes("search") ||
      name.includes("find") ||
      name.includes("grep")
    )
      return Search;
    if (name.includes("code") || name.includes("view")) return Code;
    if (
      name.includes("directory") ||
      name.includes("list") ||
      name.includes("folder")
    )
      return Folder;
    if (
      name.includes("database") ||
      name.includes("mongo") ||
      name.includes("sql")
    )
      return Database;

    return Cpu;
  };

  // Enhanced typing animation with smart buffering
  useEffect(() => {
    if (latestToolCall && agentStatus === "generating") {
      if (currentToolCall && currentToolCall.uuid !== latestToolCall.uuid) {
        if (typeTimeoutRef.current) {
          clearTimeout(typeTimeoutRef.current);
        }

        // Smooth transition without flashing
        setCurrentToolCall(latestToolCall);
        setAnimationKey((prev) => prev + 1);
        setDisplayedContent("");

        startTypingAnimation(latestToolCall.content || "");
      } else if (!currentToolCall) {
        setCurrentToolCall(latestToolCall);
        setAnimationKey((prev) => prev + 1);
        startTypingAnimation(latestToolCall.content || "");
      }
    } else if (agentStatus !== "generating") {
      if (typeTimeoutRef.current) {
        clearTimeout(typeTimeoutRef.current);
      }
      setCurrentToolCall(null);
      setDisplayedContent("");
    }
  }, [latestToolCall, agentStatus, currentToolCall]);

  const startTypingAnimation = (content) => {
    if (content.length === 0) {
      setDisplayedContent("");
      return;
    }

    setDisplayedContent("");

    const shouldSample = content.length > 500;
    const sampleRate = shouldSample ? 2 : 1;

    let index = 0;

    const typeContent = () => {
      if (index < content.length) {
        if (shouldSample && index % sampleRate !== 0) {
          index++;
          typeTimeoutRef.current = setTimeout(typeContent, 2);
          return;
        }

        const chunkSize = shouldSample ? 8 : 3;
        const endIndex = Math.min(index + chunkSize, content.length);
        setDisplayedContent(content.substring(0, endIndex));
        index = endIndex;

        const baseSpeed = 25;
        const variation = Math.random() * 10;
        const pauseChance = Math.random();

        let speed = baseSpeed + variation;
        if (pauseChance < 0.01) {
          speed += 30;
        }

        typeTimeoutRef.current = setTimeout(typeContent, speed);
      }
    };

    typeTimeoutRef.current = setTimeout(typeContent, 200);
  };

  // Recalculate isIdle after all state is properly set
  const isIdle = !latestToolCall || agentStatus !== "generating";

  // Static neural network background effect for active state - dark mode only
  useEffect(() => {
    if (!isIdle) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      // Force canvas to fill entire container
      const resizeCanvas = () => {
        const container = canvas.parentElement;
        if (container) {
          canvas.width = container.offsetWidth;
          canvas.height = container.offsetHeight;
        }
      };

      resizeCanvas();
      window.addEventListener("resize", resizeCanvas);

      // Static neural network nodes
      const nodes = [];
      const numNodes = Math.min(
        15,
        Math.floor((canvas.width * canvas.height) / 25000),
      );

      // Initialize static nodes
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 1,
          opacity: Math.random() * 0.3 + 0.4, // Less transparent
        });
      }

      // Find connections between nearby nodes
      const connections = [];
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            connections.push({
              from: nodes[i],
              to: nodes[j],
              opacity: Math.max(0, (150 - distance) / 150) * 0.4, // Less transparent
            });
          }
        }
      }

      // Draw static network once
      const drawNetwork = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw connections first (behind nodes)
        connections.forEach((connection) => {
          ctx.strokeStyle = `rgba(132, 204, 22, ${connection.opacity})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(connection.from.x, connection.from.y);
          ctx.lineTo(connection.to.x, connection.to.y);
          ctx.stroke();
        });

        // Draw nodes
        nodes.forEach((node) => {
          // Subtle glow
          const gradient = ctx.createRadialGradient(
            node.x,
            node.y,
            0,
            node.x,
            node.y,
            node.size * 2.5,
          );
          gradient.addColorStop(0, `rgba(132, 204, 22, ${node.opacity * 0.8})`);
          gradient.addColorStop(
            0.7,
            `rgba(163, 230, 53, ${node.opacity * 0.5})`,
          );
          gradient.addColorStop(1, "rgba(132, 204, 22, 0)");

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size * 2.5, 0, Math.PI * 2);
          ctx.fill();

          // Core node
          ctx.fillStyle = `rgba(163, 230, 53, ${node.opacity})`;
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
          ctx.fill();
        });
      };

      // Draw once and on resize
      drawNetwork();
      window.addEventListener("resize", () => {
        resizeCanvas();
        drawNetwork();
      });

      return () => {
        window.removeEventListener("resize", resizeCanvas);
      };
    } else {
      // Clear canvas when idle
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isIdle]);

  const currentScreenshotUrl = isBrowserScreenshotTool(
    currentToolCall?.tool_name || currentToolCall?.tool_description,
  )
    ? firstImageUrl(currentToolCall?.images) ||
      firstImageUrl(currentToolCall?.content)
    : null;

  return (
    <div className="h-full w-full flex flex-col bg-slate-100 dark:bg-neutral-900 overflow-hidden relative min-h-0 max-w-full">
      <style>{`
        @keyframes kolegaStudioPulse {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
            filter: drop-shadow(0 0 16px #84CC16) drop-shadow(0 0 32px rgba(132, 204, 22, 0.3)); 
          }
          50% { 
            transform: scale(1.05) rotate(2deg); 
            filter: drop-shadow(0 0 24px #A3E635) drop-shadow(0 0 48px rgba(163, 230, 53, 0.4)); 
          }
        }
        
        @keyframes kolegaStudioFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes kolegaBracketPulse {
          0%, 100% { transform: translateX(0) scale(1); opacity: 0.8; }
          50% { transform: translateX(-3px) scale(1.1); opacity: 1; }
        }
        
        @keyframes kolegaSlashGlow {
          0%, 100% { 
            transform: rotate(0deg) scale(1); 
            filter: drop-shadow(0 0 6px #84CC16);
          }
          50% { 
            transform: rotate(6deg) scale(1.05); 
            filter: drop-shadow(0 0 12px #A3E635);
          }
        }
        
        @keyframes smoothPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        
        @keyframes slideUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes waveAnimation {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes typewriterCursor {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0.3; }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 0.6; transform: scale(1) rotate(180deg); }
        }
        
        @keyframes progressShine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .kolega-studio-icon {
          animation: kolegaStudioPulse 6s ease-in-out infinite, kolegaStudioFloat 8s ease-in-out infinite;
        }
        
        .kolega-bracket-left {
          animation: kolegaBracketPulse 4s ease-in-out infinite reverse;
        }
        
        .kolega-bracket-right {
          animation: kolegaBracketPulse 4s ease-in-out infinite;
        }
        
        .kolega-slash {
          animation: kolegaSlashGlow 4s ease-in-out infinite;
        }
        
        .smooth-pulse {
          animation: smoothPulse 3s ease-in-out infinite;
        }
        
        .slide-up {
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .wave-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(132, 204, 22, 0.08) 50%, 
            transparent 100%
          );
          animation: waveAnimation 4s ease-in-out infinite;
          pointer-events: none;
        }
        
        .typewriter-cursor::after {
          content: '|';
          margin-left: 2px;
          animation: typewriterCursor 1s ease-in-out infinite;
          color: #64748b;
        }
        
        .dark .typewriter-cursor::after {
          color: #A3E635;
          text-shadow: 0 0 4px rgba(163, 230, 53, 0.8);
        }
        
        .sparkle-effect {
          position: absolute;
          width: 4px;
          height: 4px;
          background: #84CC16;
          border-radius: 50%;
          animation: sparkle 2s ease-in-out infinite;
        }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(132, 204, 22, 0.2);
        }
        
        .dark .glass-morphism {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(132, 204, 22, 0.3);
        }
        
        .tool-badge {
          background: linear-gradient(135deg, rgba(132, 204, 22, 0.2) 0%, rgba(163, 230, 53, 0.2) 100%);
          border: 1px solid rgba(132, 204, 22, 0.3);
          backdrop-filter: blur(10px);
        }
        
        .dark .tool-badge {
          background: linear-gradient(135deg, rgba(132, 204, 22, 0.15) 0%, rgba(163, 230, 53, 0.15) 100%);
          border: 1px solid rgba(132, 204, 22, 0.4);
        }
        
        .progress-bar {
          position: relative;
          height: 2px;
          background: rgba(132, 204, 22, 0.1);
          overflow: hidden;
        }
        
        .progress-bar::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 30%;
          background: linear-gradient(90deg, transparent, #84CC16, transparent);
          animation: progressShine 1.5s ease-in-out infinite;
        }
        
        .monitor-scrollbar::-webkit-scrollbar {
          width: 0px;
        }
        
        .monitor-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .monitor-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(132, 204, 22, 0.2);
          border-radius: 4px;
        }
        
        .monitor-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(132, 204, 22, 0.4);
        }
        
        .content-gradient {
          background: linear-gradient(135deg, 
            rgba(132, 204, 22, 0.03) 0%, 
            transparent 50%,
            rgba(163, 230, 53, 0.03) 100%
          );
        }
        
        .dark .content-gradient {
          background: linear-gradient(135deg, 
            rgba(132, 204, 22, 0.05) 0%, 
            transparent 50%,
            rgba(163, 230, 53, 0.05) 100%
          );
        }
      `}</style>

      {/* Subtle neural network background effect for dark mode only - full background */}
      <canvas
        ref={canvasRef}
        className={`absolute inset-0 pointer-events-none opacity-30 hidden dark:block ${isIdle ? "!hidden" : ""}`}
        style={{
          zIndex: 0,
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {isIdle ? (
        // V5 idle state - clean and professional
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 relative">
          <div className="flex items-center mb-8 group">
            <div className="relative mr-4">
              <div className="kolega-studio-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="kolega-bracket-left"
                    d="M7 8L3 12L7 16"
                    stroke="#A3E635"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    className="kolega-bracket-right"
                    d="M17 8L21 12L17 16"
                    stroke="#A3E635"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    className="kolega-slash"
                    d="M10 20L14 4"
                    stroke="#A3E635"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-4 h-4 text-purple-500/60 animate-pulse" />
              <Bot className="absolute -bottom-2 -left-2 w-4 h-4 text-purple-400/60 animate-pulse animation-delay-200" />
            </div>

            <h2 className="text-4xl font-bold text-ui-text-primary ai-text">
              <span className="mr-2 font-semibold">Kolega</span>
              <span className="font-light text-neutral-500 dark:text-neutral-400">
                Studio
              </span>
            </h2>
          </div>

          <div className="text-sm text-neutral-500 dark:text-neutral-400 ai-text">
            <div className="flex items-center gap-2 justify-center">
              <Activity className="w-4 h-4 text-lime-400 animate-pulse" />
              <span>AI Assistant Ready</span>
            </div>
          </div>
        </div>
      ) : (
        // Active state - cleaner styling with less glow
        <div
          className="flex-1 flex flex-col overflow-hidden relative min-h-0 w-full max-w-full"
          style={{ zIndex: 1 }}
        >
          {currentToolCall && (
            <>
              {/* Cleaner tool header */}
              <div className="bg-slate-100 dark:bg-black/90 border-b border-gray-200 dark:border-lime-500/20 px-6 py-4 shadow-sm w-full backdrop-blur-sm">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {/* Simpler icon container */}
                    <div className="relative flex-shrink-0">
                      <div className="relative bg-gray-50 dark:bg-lime-500/5 backdrop-blur-xl rounded-lg p-3 border border-gray-200 dark:border-lime-500/30 shadow-sm">
                        {React.createElement(
                          getToolIcon(
                            currentToolCall.tool_name ||
                              currentToolCall.tool_description,
                          ),
                          {
                            className:
                              "w-6 h-6 text-gray-700 dark:text-lime-400/90 transition-all duration-300",
                          },
                        )}
                      </div>
                    </div>

                    {/* Tool info - less glow */}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-xs font-medium text-gray-500 dark:text-lime-400/60">
                        TOOL
                      </span>
                      <h3 className="text-lg font-bold text-gray-800 dark:text-lime-400/90 mt-1 transition-all duration-300 truncate">
                        {(
                          currentToolCall.tool_name ||
                          currentToolCall.tool_description ||
                          "Unknown Tool"
                        )
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(" ")}
                      </h3>
                    </div>
                  </div>

                  {/* Simpler Active Status Tag */}
                  <div className="px-3 py-1 rounded-full bg-gray-100 dark:bg-lime-500/10 border border-gray-300 dark:border-lime-500/30 backdrop-blur-sm shadow-sm flex-shrink-0">
                    <span className="text-xs font-bold text-lime-600 dark:text-lime-400/80 animate-pulse">
                      ACTIVE
                    </span>
                  </div>
                </div>

                {/* Progress bar with less glow */}
                <div className="mt-3 h-[2px] bg-gray-200 dark:bg-lime-500/10 rounded-full overflow-hidden w-full">
                  <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-lime-500 dark:via-lime-400/70 to-transparent animate-[progressShine_1.5s_ease-in-out_infinite]" />
                </div>
              </div>

              {/* Content area - transparent background for dark mode */}
              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-auto p-6 monitor-scrollbar bg-slate-100 dark:bg-transparent relative w-full max-w-full"
              >
                {/* Tool output content */}
                {currentScreenshotUrl ? (
                  <div className="flex justify-center">
                    <div className="relative group">
                      <img
                        src={currentScreenshotUrl}
                        alt="Screenshot"
                        className="relative max-w-full h-auto rounded-xl shadow-2xl ring-1 ring-gray-200 dark:ring-lime-500/20 transition-all duration-300 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4 bg-white/95 dark:bg-black/80 backdrop-blur-xl rounded-lg px-4 py-2 border border-gray-200 dark:border-lime-500/30 shadow-lg">
                        <div className="flex items-center gap-2">
                          <Image className="w-4 h-4 text-gray-600 dark:text-lime-400/80" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-transparent rounded-xl p-6 border border-gray-200 dark:border-lime-500/10 shadow-lg w-full max-w-full">
                    <div className="flex items-center gap-2 mb-4">
                      <Code className="w-4 h-4 text-gray-600 dark:text-lime-400/70" />
                      <span className="text-xs font-bold text-gray-600 dark:text-lime-400/60 tracking-wider">
                        OUTPUT STREAM
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-gray-300 dark:from-lime-500/30 to-transparent" />
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-sm font-mono leading-relaxed text-gray-800 dark:text-lime-400/90 w-full max-w-full overflow-hidden">
                      <span
                        key={animationKey}
                        className={
                          currentToolCall.isStreaming === true ||
                          currentToolCall.is_streaming === true ||
                          (typeof currentToolCall.isStreaming === "undefined" &&
                            typeof currentToolCall.is_streaming !==
                              "undefined" &&
                            currentToolCall.is_streaming === true)
                            ? "typewriter-cursor"
                            : ""
                        }
                      >
                        {displayedContent}
                      </span>
                    </pre>
                  </div>
                )}
              </div>

              {/* Simpler status bar - removed LAYER and MS */}
              <div className="px-6 py-3 bg-slate-100 dark:bg-black/90 border-t border-gray-200 dark:border-lime-500/20 shadow-inner flex justify-between text-xs backdrop-blur-sm">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Binary className="w-4 h-4 text-gray-500 dark:text-lime-400/70" />
                    <span className="font-mono text-gray-500 dark:text-lime-400/60">
                      {displayedContent.length} BYTES
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Monitor;
